import { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartTooltip,
  ResponsiveContainer,
  LabelList
} from 'recharts';
import { useGetBreakdown, useGetTopKeywords } from '@/apis/business/agent/signals/hook';
import { BreakdownItem } from '@/apis/business/agent/signals/types';
import { titleCase } from 'utils/helperFunctions';
import { MdSearch } from 'react-icons/md';
import { IoChevronDown, IoClose } from 'react-icons/io5';
import { SignalsInsightsBreakdownSkeleton, SignalsInsightsChartSkeleton } from './SignalsSkeletons';

function BreakdownCard({
  title,
  data,
  color
}: {
  title: string;
  data: BreakdownItem[];
  total?: number;
  color: string;
}) {
  return (
    <Grid size={{ xs: 12, md: 6, lg: 3 }}>
      <Box border='1px solid #ECECEC' bgcolor='#FAFAFA' borderRadius={2} p={2}>
        <Typography color='text.secondary' fontSize={13} mb={1}>
          {title.toUpperCase()}
        </Typography>
        <Stack gap={1.5}>
          {data.length ? (
            data.map((item) => (
              <Box key={item.label}>
                <Stack direction='row' justifyContent='space-between'>
                  <Typography fontSize={14} fontWeight={500}>
                    {titleCase(item.label)}
                  </Typography>
                  <Typography fontSize={14} color='text.secondary'>
                    {item.count} · {item.percentage}%
                  </Typography>
                </Stack>
                <Box height={4} borderRadius={20} bgcolor='#E5E7EB' mt={0.5}>
                  <Box height='100%' width={`${item.percentage}%`} bgcolor={color} borderRadius={20} />
                </Box>
              </Box>
            ))
          ) : (
            <Typography fontSize={14} color='text.secondary'>
              No data available
            </Typography>
          )}
        </Stack>
      </Box>
    </Grid>
  );
}

interface SignalsInsightsProps {
  startDate: string;
  endDate: string;
}

export default function SignalsInsights({ startDate, endDate }: SignalsInsightsProps) {
  const theme = useTheme();
  const dateParams = useMemo(() => ({ start_date: startDate, end_date: endDate }), [startDate, endDate]);

  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const breakdownParams = useMemo(
    () => ({ ...dateParams, ...(selectedKeywords.length ? { keywords: selectedKeywords } : {}) }),
    [dateParams, selectedKeywords]
  );

  const { data: breakdown, isLoading: breakdownLoading } = useGetBreakdown(breakdownParams);
  const { data: topKeywords, isLoading: keywordsLoading } = useGetTopKeywords(dateParams);

  const filteredKeywords = useMemo(
    () => (topKeywords || []).filter((k) => k.keyword.toLowerCase().includes(keywordSearch.toLowerCase())),
    [topKeywords, keywordSearch]
  );

  const keywordCountMap = useMemo(
    () => new Map((topKeywords || []).map((item) => [item.keyword, item.count])),
    [topKeywords]
  );

  const toggleKeyword = (keyword: string) => {
    setSelectedKeywords((prev) => (prev.includes(keyword) ? prev.filter((k) => k !== keyword) : [...prev, keyword]));
  };

  const volumeData = useMemo(
    () => (topKeywords || []).slice(0, 8).map((item) => ({ name: titleCase(item.keyword), value: item.count })),
    [topKeywords]
  );

  const totalCalls = breakdown?.total_calls ?? 0;

  return (
    <>
      <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={2.5} mb={3}>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography fontSize={20} fontWeight={500}>
            Breakdown across all keywords
          </Typography>
          <Chip
            label={`${totalCalls} Calls`}
            variant='outlined'
            sx={{ borderColor: '#ECECEC', borderRadius: 48, bgcolor: '#F8F9FA' }}
          />
        </Stack>
        <Typography color='text.secondary' fontSize={14} mb={2}>
          Click any keyword chip below to focus the breakdown
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent='space-between'
          alignItems={{ xs: 'start', md: 'center' }}
          gap={1.5}
          mb={2}
        >
          <Stack direction='row' gap={1} alignItems='center' flexWrap='wrap'>
            {selectedKeywords.length ? (
              selectedKeywords.map((keyword) => (
                <Chip
                  key={keyword}
                  label={
                    <Stack direction='row' alignItems='center' gap={1}>
                      <Typography
                        component='span'
                        sx={{ fontSize: 13, fontWeight: 500, lineHeight: '20px', color: 'inherit' }}
                      >
                        {titleCase(keyword)}
                      </Typography>
                      <Box
                        component='span'
                        sx={{
                          minWidth: 22,
                          height: 22,
                          py: 0,
                          px: '12px',
                          borderRadius: '999px',
                          bgcolor: '#fff',
                          color: '#374151',
                          fontSize: 10,
                          fontWeight: 500,
                          lineHeight: '22px',
                          textAlign: 'center'
                        }}
                      >
                        {keywordCountMap.get(keyword) ?? 0}
                      </Box>
                    </Stack>
                  }
                  onDelete={() => toggleKeyword(keyword)}
                  deleteIcon={<IoClose size={16} />}
                  sx={{
                    height: 42,
                    border: `1px solid ${theme.palette.primary.main}`,
                    bgcolor: theme.palette.primary.light,
                    color: theme.palette.primary.main,
                    borderRadius: 999,
                    '& .MuiChip-label': {
                      py: 0
                    },
                    '& .MuiChip-deleteIcon': {
                      color: theme.palette.primary.main,
                      width: 20,
                      height: '100%',

                      mr: 1,
                      ml: -1,

                      borderRadius: 0
                    }
                  }}
                />
              ))
            ) : (
              <Typography color='text.secondary' fontSize={15}>
                No keyword selected
              </Typography>
            )}
          </Stack>

          {/* Keyword multi-select dropdown */}
          <Box minWidth={{ xs: '100%', md: 340 }}>
            <Box
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                border: `1px solid ${theme.palette.primary.light}`,
                borderRadius: '10px',
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                bgcolor: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography fontSize={14} color={selectedKeywords.length ? 'text.primary' : 'text.secondary'}>
                {keywordsLoading
                  ? 'Loading...'
                  : selectedKeywords.length
                    ? `${selectedKeywords.length} keyword${selectedKeywords.length > 1 ? 's' : ''} selected`
                    : `Search ${topKeywords?.length ?? 0} keywords...`}
              </Typography>
              <IoChevronDown size={16} color='#6B7280' />
            </Box>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => {
                setAnchorEl(null);
                setKeywordSearch('');
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: {
                    width: anchorEl?.offsetWidth ?? 340,
                    mt: 0.5,
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                  }
                }
              }}
            >
              <Box p={1}>
                <TextField
                  size='small'
                  fullWidth
                  placeholder='Search keywords...'
                  value={keywordSearch}
                  onChange={(e) => setKeywordSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <MdSearch />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
              <Divider />
              <List dense sx={{ maxHeight: 260, overflowY: 'auto', py: 0 }}>
                {filteredKeywords.map((item) => (
                  <ListItem
                    key={item.keyword}
                    disablePadding
                    onClick={() => toggleKeyword(item.keyword)}
                    sx={{ cursor: 'pointer', px: 1, '&:hover': { bgcolor: '#F9FAFB' } }}
                  >
                    <Checkbox
                      size='small'
                      checked={selectedKeywords.includes(item.keyword)}
                      disableRipple
                      sx={{ p: 0.5 }}
                    />
                    <ListItemText
                      primary={
                        <Stack direction='row' justifyContent='space-between'>
                          <Typography fontSize={14}>{titleCase(item.keyword)}</Typography>
                          <Typography
                            fontSize={12}
                            fontWeight={600}
                            color='text.secondary'
                            sx={{ bgcolor: '#F3F4F6', px: 1, py: 0.2, borderRadius: 999 }}
                          >
                            {item.count}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                ))}
                {filteredKeywords.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography fontSize={14} color='text.secondary'>
                          No keywords found
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Popover>
          </Box>
        </Stack>

        {breakdownLoading ? (
          <SignalsInsightsBreakdownSkeleton />
        ) : (
          <Grid container spacing={2}>
            <BreakdownCard title='Persona' data={breakdown?.persona ?? []} total={totalCalls} color='#22C55E' />
            <BreakdownCard title='Sentiment' data={breakdown?.sentiment ?? []} total={totalCalls} color='#195EFF' />
            <BreakdownCard title='Severity' data={breakdown?.severity ?? []} total={totalCalls} color='#F59E0B' />
            <BreakdownCard title='Workflows' data={breakdown?.workflows ?? []} total={totalCalls} color='#7C3AED' />
          </Grid>
        )}
      </Box>

      <Box bgcolor='#fff' border='1px solid #ECECEC' borderRadius={2} p={2.5}>
        <Typography fontSize={20} fontWeight={500} pb={0.5}>
          Top Keywords by Volume
        </Typography>
        <Typography color='text.secondary' fontSize={14} mb={2}>
          Most mentioned themes across all conversations
        </Typography>

        {keywordsLoading ? (
          <SignalsInsightsChartSkeleton />
        ) : (
          <ResponsiveContainer width='100%' height={340}>
            <BarChart data={volumeData} layout='vertical' margin={{ left: 0, right: 30, top: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray='3 3' vertical={false} />
              <XAxis type='number' allowDecimals={false} />
              <YAxis type='category' dataKey='name' width={200} style={{ fontSize: 14 }} />
              <RechartTooltip />
              <Bar dataKey='value' fill='#195EFF' radius={[0, 8, 8, 0]} barSize={28}>
                <LabelList dataKey='value' content={() => null} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>
    </>
  );
}
